import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Container,
    Row,
    Col,
    Button,
    Spinner,
    Modal,
    Form,
    Badge,
} from "react-bootstrap";

import { getAuthToken } from "../../utils/auth";


const ExpenseManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [confirmReimburse, setConfirmReimburse] = useState(null);

    const [form, setForm] = useState({
        title: "",
        description: "",
        amount: "",
        category: "OTHER",
    });

    const categories = [
        "SERVER",
        "MARKETING",
        "DOMAIN",
        "DESIGN",
        "OTHER",
    ];



    // =========================
    // FETCH
    // =========================

    const fetchExpenses = useCallback(async () => {
        try {
            setLoading(true);

            const res = await fetch(`${baseUrl}/expenses`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await res.json();

            if (result.status) {
                setExpenses(result.data || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token]);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    // =========================
    // FORM
    // =========================

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleAddExpense = async () => {
        try {
            const res = await fetch(`${baseUrl}/expenses`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...form,
                    amount: Number(form.amount),

                }),
            });

            const result = await res.json();

            if (result.status) {
                setShowModal(false);

                setForm({
                    title: "",
                    description: "",
                    amount: "",
                    category: "OTHER",
                });

                fetchExpenses();
            }
        } catch (e) {
            console.error(e);
        }
    };

    // =========================
    // REIMBURSE
    // =========================

    const reimburseExpense = async (id) => {
        try {
            const res = await fetch(
                `${baseUrl}/expenses/${id}/reimburse`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await res.json();

            if (result.status) {
                setConfirmReimburse(null);
                fetchExpenses();
            }
        } catch (e) {
            console.error(e);
        }
    };

    // =========================
    // TOTAL SUM
    // =========================

    const total = expenses.reduce(
        (sum, e) => sum + Number(e.amount),
        0
    );

    return (
        <Container className="mt-4">

            {/* HEADER */}

            <Row className="mb-3">
                <Col>
                    <h3>Expenses</h3>
                </Col>

                <Col className="text-end">
                    <Button onClick={() => setShowModal(true)}>
                        Add Expense
                    </Button>
                </Col>
            </Row>

            {/* LOADING */}

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table striped bordered hover>

                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Title</th>
                            <th>Amount</th>
                            <th>Category</th>
                            <th>Date</th>
                            <th>Paid By</th>
                            <th>Reimbursed</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>

                        {expenses.map((e) => (
                            <tr key={e.id}>

                                <td>{e.id}</td>
                                <td>{e.title}</td>

                                <td>{e.amount}</td>

                                <td>{e.category}</td>

                                <td>{e.date}</td>

                                <td>{e.paidByName}</td>

                                <td>
                                    {e.dateReimbursed
                                        ? e.dateReimbursed
                                        : "-"}
                                </td>

                                <td>

                                    {e.dateReimbursed ? (

                                        <Badge bg="secondary">
                                            Reimbursed
                                        </Badge>

                                    ) : (

                                        <Button
                                            size="sm"
                                            variant="success"
                                            onClick={() => setConfirmReimburse(e)}
                                        >
                                            Reimburse
                                        </Button>

                                    )}

                                </td>

                            </tr>
                        ))}

                    </tbody>

                    {/* TOTAL FOOTER */}

                    <tfoot>
                        <tr>
                            <td colSpan="2">
                                <b>Total</b>
                            </td>

                            <td>
                                <b>{total}</b>
                            </td>

                            <td colSpan="5"></td>
                        </tr>
                    </tfoot>

                </Table>
            )}

            {/* ===================== */}
            {/* ADD MODAL */}
            {/* ===================== */}

            <Modal show={showModal} onHide={() => setShowModal(false)}>

                <Modal.Header closeButton>
                    <Modal.Title>Add Expense</Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <Form>

                        <Form.Group className="mb-2">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                                type="number"
                                name="amount"
                                value={form.amount}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>Category</Form.Label>

                            <Form.Select
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                            >
                                {categories.map((c) => (
                                    <option key={c}>
                                        {c}
                                    </option>
                                ))}
                            </Form.Select>

                        </Form.Group>

                    </Form>

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleAddExpense}
                    >
                        Save
                    </Button>

                </Modal.Footer>

            </Modal>

            {/* ===================== */}
            {/* CONFIRM MODAL */}
            {/* ===================== */}

            <Modal
                show={confirmReimburse != null}
                onHide={() => setConfirmReimburse(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Confirm Reimburse
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Are you sure you want to reimburse expense
                    #{confirmReimburse?.id} ?
                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setConfirmReimburse(null)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="success"
                        onClick={() =>
                            reimburseExpense(
                                confirmReimburse.id
                            )
                        }
                    >
                        Yes, Reimburse
                    </Button>

                </Modal.Footer>
            </Modal>

        </Container>
    );
};

export default ExpenseManagement;